import { datadogRum } from '@datadog/browser-rum';

// #3458: measuring email-driven visits. Email links carry
// `utm_source=email&utm_campaign=<type key>` (links.ts). When such a URL is
// open for a signed-in user, the browser records one Datadog RUM custom action
// carrying the email type. Resend click tracking stays off, so this is the only
// click signal.

export const EMAIL_LINK_LANDED_ACTION = 'email.link_landed';

/** Type keys have the email_kinds key shape; anything else is not ours. */
const KIND_KEY = /^[a-z][a-z0-9_]{0,62}$/;
const STORAGE_PREFIX = 'btc:email-landing:';

/** The email type a URL's query was tagged with, or null for any other visit. */
export function emailLandingKind(search: URLSearchParams): string | null {
  if (search.get('utm_source') !== 'email') return null;
  const campaign = search.get('utm_campaign') ?? '';
  return KIND_KEY.test(campaign) ? campaign : null;
}

// Used only when session storage is unavailable (a blocked or private
// context), so a re-render in that tab still cannot record twice.
const landedInMemory = new Set<string>();

/** Marks a landing as recorded for this tab. False when it already was. */
function claimLanding(key: string): boolean {
  const storageKey = STORAGE_PREFIX + key;
  try {
    const storage = window.sessionStorage;
    if (storage.getItem(storageKey) !== null) return false;
    storage.setItem(storageKey, '1');
    return true;
  } catch {
    if (landedInMemory.has(storageKey)) return false;
    landedInMemory.add(storageKey);
    return true;
  }
}

/**
 * Records one landing per URL per tab session: a refresh or a re-render of
 * the same URL is not a second visit. `url` is the pathname plus query and
 * stays in this tab's session storage; only the type reaches Datadog.
 */
export function recordEmailLanding(kind: string, url: string): void {
  if (!claimLanding(url)) return;
  try {
    datadogRum.addAction(EMAIL_LINK_LANDED_ACTION, { email_type: kind, utm_campaign: kind, utm_source: 'email' });
  } catch {
    // Telemetry never breaks the page.
  }
}
